import { NoSymbolIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useFlag } from '../../hooks/useFeatureFlags';
import { SPENDING_PAGE_PATH, spendingRefusalMessage } from '../../utils/spending';

interface SpendingLimitPromptProps {
  onDismiss: () => void;
}

/**
 * SpendingLimitPrompt (task 1159) is shown when innovate-service returns
 * 402 { error: 'spending_limit_reached' } — the account set a monthly spending
 * limit with the hard stop on, and this month's billed usage has reached it.
 * Sibling of UpgradePrompt and ChoosePlanPrompt (same placement, rendered by
 * EntitlementGuard), but the remedy is the Spending page, not a plan change.
 *
 * Role-aware through `innovate.fe.account.spending` (task 1217; owners and admins, who can
 * raise the limit): they get the Spending page named, and a link when the page
 * is open to them; members are told to contact the account owner.
 */
export default function SpendingLimitPrompt({ onDismiss }: SpendingLimitPromptProps) {
  const canManage = useFlag('innovate.fe.account.spending');
  const canOpenSpending = useFlag('innovate.fe.spending.page');

  return (
    <div
      role="alert"
      data-testid="spending-limit-prompt"
      className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-4"
    >
      <div className="flex items-start gap-3">
        <NoSymbolIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">Refused: monthly spending limit reached</p>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300" data-testid="spending-limit-prompt-message">
            {spendingRefusalMessage(canManage)}
          </p>
          {canManage && canOpenSpending && (
            <div className="mt-3">
              {/* Plain anchor (not router Link), like the sibling prompts. */}
              <a
                href={SPENDING_PAGE_PATH}
                data-testid="spending-limit-prompt-link"
                className="inline-flex items-center px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-md transition-colors"
              >
                Open Spending
              </a>
            </div>
          )}
        </div>
        <button
          onClick={onDismiss}
          data-testid="spending-limit-prompt-dismiss"
          className="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          aria-label="Dismiss"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
